// Player List Component for Helix 2000

import React, { useState } from 'react';
import { Helix2000Character } from '../../../shared/CharacterTypes';
import { COLORS, RAW, classColor, classIcon, formatCallingName } from '../theme';
import { useWithLiveVitals } from '../hooks/usePlayerVital';
import { readPlayerVitals } from '../../systems/player/livePlayerVitals';

interface PlayerListProps {
  currentPlayer: Helix2000Character | null;
  otherPlayers: Map<string, Helix2000Character>;
}

const PlayerListInner: React.FC<PlayerListProps> = ({
  currentPlayer: reactPlayer,
  otherPlayers
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  // Own HP/MP come from the vitals store (plan 21 §8.3), not the props.
  const currentPlayer = useWithLiveVitals(reactPlayer);

  const formatClassName = (className: string) => formatCallingName(className);

  const getClassColor = (className: string) => classColor(className);

  const getClassEmoji = (className: string) => classIcon(className);

  const allPlayers = currentPlayer ? [
    { ...currentPlayer, isCurrent: true, isOnline: true },
    ...Array.from(otherPlayers.values()).map(p => ({ ...p, isCurrent: false, isOnline: p.isOnline ?? true }))
  ] : Array.from(otherPlayers.values()).map(p => ({ ...p, isCurrent: false, isOnline: p.isOnline ?? true }));

  return (
    <div data-hud-surface="player-list" style={{
      position: 'absolute',
      right: 10,
      top: 10,
      width: isCollapsed ? '40px' : '220px',
      maxHeight: isCollapsed ? 'auto' : '400px',
      padding: isCollapsed ? '10px 5px' : '20px',
      background: 'rgba(0, 0, 0, 0.6)',
      pointerEvents: 'auto',
      color: RAW.cloud,
      overflowY: 'auto',
      borderLeft: '1px solid rgba(241, 196, 15, 0.3)',
      zIndex: 90,
      transition: 'width 0.3s ease, padding 0.3s ease'
    }}>
      {/* Header with collapse toggle */}
      <div
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{
          margin: isCollapsed ? '0 0 10px 0' : '0 0 15px 0',
          color: COLORS.textAccent,
          textAlign: 'center',
          borderBottom: '1px solid rgba(241, 196, 15, 0.3)',
          paddingBottom: isCollapsed ? '8px' : '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: isCollapsed ? '12px' : '15px',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        title={isCollapsed ? 'Expand' : 'Collapse'}
      >
        <span>{isCollapsed ? '👥' : '👥 Players'}</span>
        {!isCollapsed && <span style={{ fontSize: '12px', opacity: 0.7 }}>({allPlayers.length})</span>}
        <span style={{ marginLeft: 'auto', fontSize: '10px' }}>
          {isCollapsed ? '◀' : '▶'}
        </span>
      </div>

      {!isCollapsed && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          fontSize: '13px'
        }}>
          {allPlayers.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '20px 10px',
              color: RAW.slate,
              fontStyle: 'italic',
              border: '1px dashed rgba(127, 140, 141, 0.3)',
              borderRadius: '6px'
            }}>
              No players online
            </div>
          ) : (
            allPlayers.map((player, index) => (
              <div
                key={player.id || `player-${index}`}
                style={{
                  padding: '12px',
                  background: player.isCurrent
                    ? 'linear-gradient(135deg, rgba(241, 196, 15, 0.15), rgba(241, 196, 15, 0.05))'
                    : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: player.isCurrent
                    ? '1px solid rgba(241, 196, 15, 0.4)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
              >
                {player.isCurrent && (
                  <div style={{
                    position: 'absolute',
                    top: '6px',
                    right: '8px',
                    fontSize: '10px',
                    color: COLORS.textAccent,
                    background: 'rgba(241, 196, 15, 0.2)',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontWeight: 'bold'
                  }}>
                    YOU
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    fontSize: '20px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '50%'
                  }}>
                    {getClassEmoji(player.class)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: player.isCurrent ? COLORS.textAccent : RAW.cloud,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span style={{
                        textDecoration: player.isOnline === false ? 'line-through' : 'none',
                        opacity: player.isOnline === false ? 0.6 : 1
                      }}>
                        {player.isOnline === false ? '🔴 ' : '🟢 '}
                        {player.name}
                      </span>
                      <span style={{
                        fontSize: '11px',
                        color: '#bdc3c7'
                      }}>
                        (Lv.{player.level})
                      </span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: getClassColor(player.class),
                      fontWeight: '500'
                    }}>
                      {formatClassName(player.class)}
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '8px',
                  marginTop: '8px',
                  flexDirection: 'column'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '11px'
                  }}>
                    <span style={{ color: COLORS.health }}>HP</span>
                    <span style={{ color: '#c0392b' }}>
                      {Math.round(player.health)}/{Math.round(player.maxHealth)}
                    </span>
                  </div>
                  <div style={{
                    height: '3px',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div
                      style={{
                        width: `${Math.max(0, Math.min(100, (player.health / player.maxHealth) * 100))}%`,
                        height: '100%',
                        backgroundColor: COLORS.health,
                        transition: 'width 0.2s ease'
                      }}
                    />
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '11px',
                    marginTop: '4px'
                  }}>
                    <span style={{ color: COLORS.mana }}>MP</span>
                    <span style={{ color: '#2980b9' }}>
                      {player.mana}/{player.maxMana}
                    </span>
                  </div>
                  <div style={{
                    height: '3px',
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div
                      style={{
                        width: `${Math.max(0, Math.min(100, (player.mana / player.maxMana) * 100))}%`,
                        height: '100%',
                        backgroundColor: COLORS.mana,
                        transition: 'width 0.2s ease'
                      }}
                    />
                  </div>
                </div>


              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * PlayerList memo — the multiplayerDiagnostics hook republishes a fresh
 * `otherPlayers` Map every 250ms (4Hz) regardless of whether anyone
 * joined/left/changed, which without this memo re-renders the whole list
 * (array spread + N child divs) 4×/sec even in a stable session. The
 * equality function skips re-render when the visible roster + vitals
 * slices are unchanged, mirroring the PlayerInfoPanel / VitalsPanel
 * pattern. Identity short-circuits the common no-change case.
 *
 * This is a static foundation fix from the Netcode B.δ.4 render-cost
 * audit — NOT the jitter attribution. The escalating-rAF-spike symptom
 * still requires live DJ instrumented-session plots (PosDriftOverlay /
 * pos-drift-recorder / session-recorder) to prioritize per the Lever 2
 * decision matrix.
 */
// Exported for direct unit testing of the memo equality logic.
export function playerListEqual(prev: PlayerListProps, next: PlayerListProps): boolean {
  // Identity short-circuit — same React-state references mean nothing changed.
  if (prev.currentPlayer === next.currentPlayer && prev.otherPlayers === next.otherPlayers) {
    return true;
  }
  // Roster size change always re-renders (join/leave).
  if (prev.otherPlayers.size !== next.otherPlayers.size) return false;
  // Compare the currentPlayer header and vitals. Its HP/MP come from the
  // vitals store once a server has sent them (both sides then read the same
  // values; useWithLiveVitals re-renders the list), else from the objects.
  const a = prev.currentPlayer;
  const b = next.currentPlayer;
  if (a !== b) {
    if (a === null || b === null) return false;
    const va = readPlayerVitals(a);
    const vb = readPlayerVitals(b);
    if (
      (a.id || a._id) !== (b.id || b._id) ||
      Math.round(va.health) !== Math.round(vb.health) ||
      va.maxHealth !== vb.maxHealth ||
      Math.round(va.mana) !== Math.round(vb.mana) ||
      va.maxMana !== vb.maxMana ||
      a.name !== b.name ||
      a.class !== b.class ||
      a.level !== b.level ||
      a.isOnline !== b.isOnline
    ) {
      return false;
    }
  }
  // Compare remote roster by iterating next and checking prev for matching
  // entries with equal vitals/header. If any id is missing or a visible
  // field differs, re-render.
  let rosterUnchanged = true;
  for (const [id, p] of next.otherPlayers) {
    const q = prev.otherPlayers.get(id);
    if (!q) {
      rosterUnchanged = false;
      break;
    }
    if (
      p.name !== q.name ||
      p.class !== q.class ||
      p.level !== q.level ||
      Math.round(p.health) !== Math.round(q.health) ||
      p.maxHealth !== q.maxHealth ||
      Math.round(p.mana) !== Math.round(q.mana) ||
      p.maxMana !== q.maxMana ||
      (p.isOnline ?? true) !== (q.isOnline ?? true)
    ) {
      rosterUnchanged = false;
      break;
    }
  }
  return rosterUnchanged;
}

export const PlayerList = React.memo(PlayerListInner, playerListEqual);
PlayerList.displayName = 'PlayerList';
